import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { getNextQueueSlot, listQueues } from './api';
import type { ComposerState, ComposerTarget } from './composerState';
import { QueueSlotsEditor } from './QueueSlotsEditor';
import { ScheduleSection } from './ScheduleSection';
import type { PublishMode } from '../../types/publishing';

// Publishing composer — the dispatch bar: Draft / Schedule / Publish now / Queue,
// the schedule section (§E — apply-to-all + per-destination times), the queue
// picker (with next-slot preview + slot editor), and the submit button.

const MODES: { value: PublishMode; label: string }[] = [
  { value: 'draft', label: 'Save draft' },
  { value: 'schedule', label: 'Schedule' },
  { value: 'now', label: 'Publish now' },
  { value: 'queue', label: 'Add to queue' },
];

export function PublishBar({
  state,
  targets,
  profileId,
  onChange,
  onSubmit,
  submitting,
  canSubmit,
}: {
  state: ComposerState;
  targets: ComposerTarget[];
  profileId: string | null;
  onChange: (updater: (s: ComposerState) => ComposerState) => void;
  onSubmit: () => void;
  submitting: boolean;
  canSubmit: boolean;
}) {
  const [slotsOpen, setSlotsOpen] = useState(false);

  const queues = useQuery({
    queryKey: ['publishing-queues', profileId],
    queryFn: () => listQueues(profileId!),
    enabled: state.publishMode === 'queue' && !!profileId,
  });

  const nextSlot = useQuery({
    queryKey: ['publishing-next-slot', profileId, state.queueId],
    queryFn: () => getNextQueueSlot(profileId!, state.queueId ?? undefined),
    enabled: state.publishMode === 'queue' && !!profileId,
  });

  const submitLabel =
    state.publishMode === 'now'
      ? 'Publish now'
      : state.publishMode === 'schedule'
        ? 'Schedule'
        : state.publishMode === 'queue'
          ? 'Add to queue'
          : 'Save draft';

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap gap-1">
        {MODES.map((m) => (
          <Button
            key={m.value}
            type="button"
            size="sm"
            variant={state.publishMode === m.value ? 'default' : 'outline'}
            onClick={() => onChange((s) => ({ ...s, publishMode: m.value }))}
          >
            {m.label}
          </Button>
        ))}
      </div>

      {state.publishMode === 'schedule' && (
        <ScheduleSection
          scheduledFor={state.scheduledFor}
          targets={targets}
          onChangeAll={(scheduledFor) => onChange((s) => ({ ...s, scheduledFor }))}
          onChangeTarget={(dest, scheduledFor) =>
            onChange((s) => ({
              ...s,
              targets: { ...s.targets, [dest]: { ...s.targets[dest], scheduledFor } },
            }))
          }
        />
      )}

      {state.publishMode === 'queue' && (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <select
              value={state.queueId ?? ''}
              className="h-9 flex-1 rounded-md border border-slate-200 px-2 text-sm"
              onChange={(e) => onChange((s) => ({ ...s, queueId: e.target.value || null }))}
            >
              <option value="">Default queue</option>
              {(queues.data ?? []).map((q) => (
                <option key={q.queueId} value={q.queueId}>
                  {q.name}
                  {q.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </select>
            <Button type="button" variant="outline" size="sm" onClick={() => setSlotsOpen(true)} disabled={!profileId}>
              Manage slots
            </Button>
          </div>
          {nextSlot.data && (
            <p className="text-xs text-slate-500">
              Next open slot: {new Date(nextSlot.data).toLocaleString()}
            </p>
          )}
          {state.publishMode === 'queue' && nextSlot.isSuccess && !nextSlot.data && (
            <p className="text-xs text-amber-600">
              No slots defined yet — add some via “Manage slots”.
            </p>
          )}
        </div>
      )}

      <Button type="button" onClick={onSubmit} disabled={!canSubmit || submitting} className="w-full">
        {submitting ? 'Working…' : submitLabel}
      </Button>

      {profileId && (
        <QueueSlotsEditor open={slotsOpen} onClose={() => setSlotsOpen(false)} profileId={profileId} />
      )}
    </div>
  );
}
